import React, { useEffect, useMemo, useState, useContext, useRef } from "react";
import ReactDOM from "react-dom";
import { AuthContext } from "../../context/AuthContext";
import { rm_list, rm_set_status } from "../../services/roleMasterService";

import AddRole from "./AddRole";
import EditRole from "./EditRole";
import ViewRole from "./ViewRole";

import {
  MdVisibility,
  MdEdit,
  MdMoreVert,
  MdArrowDropUp,
  MdArrowDropDown,
  MdBlock,
  MdCheckCircle,
} from "react-icons/md";

import "../Users.css";

const safe = (v) => (v == null ? "" : String(v));

const sortList = (list, key, dir) =>
  [...list].sort((a, b) =>
    dir === "asc"
      ? safe(a?.[key]).localeCompare(safe(b?.[key]))
      : safe(b?.[key]).localeCompare(safe(a?.[key]))
  );

function ActionMenu({ items }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuW = 200,
      gap = 8;
    setPos({ top: r.top - 4, left: Math.max(8, r.left - menuW - gap) });
    const close = (e) => {
      if (!btnRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close, true);
    window.addEventListener("scroll", close);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", close, true);
      window.removeEventListener("scroll", close);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        className="btn btn-sm btn-action-more"
        title="Actions"
        onClick={() => setOpen((v) => !v)}
      >
        <MdMoreVert size={18} />
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            className="dropdown-menu show shadow-sm actionmenu-portal"
            style={{
              position: "fixed",
              zIndex: 5000,
              minWidth: 200,
              top: pos.top,
              left: pos.left,
            }}
          >
            {items.map((it, i) =>
              it.type === "divider" ? (
                <div key={i} className="dropdown-divider" />
              ) : (
                <button
                  key={i}
                  className={`dropdown-item ${it.className || ""}`}
                  onClick={it.onClick}
                >
                  {it.label}
                </button>
              )
            )}
          </div>,
          document.body
        )}
    </>
  );
}

export default function RoleMasterList() {
  const { hasPermission, user } = useContext(AuthContext);
  const isSuperAdmin = (user?.role_code || "").toUpperCase() === "SUPER_ADMIN";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [showAdd, setShowAdd] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [viewRole, setViewRole] = useState(null);

  const canView = isSuperAdmin && hasPermission("ROLE_MASTER_MANAGE", "view");
  const canCreate = isSuperAdmin && hasPermission("ROLE_MASTER_MANAGE", "create");
  const canEdit = isSuperAdmin && hasPermission("ROLE_MASTER_MANAGE", "edit");

  const extractArray = (res) => {
    // Handles both cases:
    // 1) axios interceptor returns body => res = {status,message,data:[...]}
    // 2) raw axios response => res = { data: {status,message,data:[...]} }
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res)) return res;
    return [];
  };

  const load = async () => {
    if (!canView) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await rm_list({ q: search, status: fStatus });
      const data = extractArray(res);
      setRows(data);
    } catch (e) {
      console.error("RoleMaster list fetch failed", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, fStatus]);

  const filtered = useMemo(() => {
    return sortList(
      rows.filter(
        (r) =>
          (!search ||
            safe(r.name).toLowerCase().includes(search.toLowerCase()) ||
            safe(r.code).toLowerCase().includes(search.toLowerCase())) &&
          (!fStatus || r.status === fStatus)
      ),
      sortKey,
      sortDir
    );
  }, [rows, search, fStatus, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const pageList = filtered.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }) => (
    <span className={`sort-icon ${sortKey === col ? "sort-active" : ""}`}>
      {sortKey === col ? (sortDir === "asc" ? <MdArrowDropUp /> : <MdArrowDropDown />) : <MdArrowDropDown />}
    </span>
  );

  const setStatus = async (r, status) => {
    await rm_set_status(r.id, status);
    load();
  };

  if (!canView) return <div className="p-4">You do not have access.</div>;
  if (loading) return <div className="p-4 text-center">Loading…</div>;

  return (
    <div className="container-fluid">
      <div className="users-hero shadow-sm">
        <div>
          <div className="h4 fw-bold mb-1 text-white">Platform Role Master</div>
          <div className="text-white-50 small">Create and manage platform-level roles</div>
        </div>
        {canCreate && (
          <button className="btn-gradient-primary" onClick={() => setShowAdd(true)}>
            + Add Role
          </button>
        )}
      </div>

      <div className="users-filter-toolbar">
        <input
          className="form-control"
          placeholder="Search role name or code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="form-select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
          <option value="">Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
        <button className="btn btn-outline-primary" onClick={load}>
          Refresh
        </button>
      </div>

      <div className="card shadow-sm">
        <table className="table table-sm users-table mb-0">
          <thead>
            <tr>
              <th onClick={() => toggleSort("name")}>
                Name <SortIcon col="name" />
              </th>
              <th onClick={() => toggleSort("code")}>
                Code <SortIcon col="code" />
              </th>
              <th>Description</th>
              <th>Status</th>
              <th onClick={() => toggleSort("created_at")}>
                Created <SortIcon col="created_at" />
              </th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageList.map((r) => (
              <tr key={r.id}>
                <td>{safe(r.name)}</td>
                <td>{safe(r.code)}</td>
                <td>{safe(r.description || "—")}</td>
                <td>
                  <span className={`badge ${r.status === "Active" ? "bg-success" : "bg-secondary"}`}>{r.status}</span>
                </td>
                <td>{safe(r.created_at).split("T")[0]}</td>
                <td className="text-end d-flex align-items-center justify-content-end gap-2">
                  <button className="btn btn-sm btn-action-view" onClick={() => setViewRole(r)}>
                    <MdVisibility size={15} />
                  </button>
                  {canEdit && (
                    <button className="btn btn-sm btn-action-edit" onClick={() => setEditRole(r)}>
                      <MdEdit size={15} />
                    </button>
                  )}
                  <ActionMenu
                    items={[
                      r.status === "Active"
                        ? {
                            label: (
                              <>
                                <MdBlock className="text-warning" size={16} /> Disable
                              </>
                            ),
                            onClick: () => setStatus(r, "Inactive"),
                          }
                        : {
                            label: (
                              <>
                                <MdCheckCircle className="text-success" size={16} /> Activate
                              </>
                            ),
                            onClick: () => setStatus(r, "Active"),
                          },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="users-pagination">
          <button disabled={page === 1} onClick={() => setPage(page - 1)}>
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i} className={page === i + 1 ? "active" : ""} onClick={() => setPage(i + 1)}>
              {i + 1}
            </button>
          ))}
          <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </div>
      </div>

      {showAdd && (
        <>
          <div className="drawer-overlay" onClick={() => setShowAdd(false)} />
          <div className="drawer-panel">
            <AddRole
              onClose={() => setShowAdd(false)}
              onSuccess={() => {
                setShowAdd(false);
                load();
              }}
            />
          </div>
        </>
      )}

      {viewRole && (
        <>
          <div className="drawer-overlay" onClick={() => setViewRole(null)} />
          <div className="drawer-panel">
            <ViewRole
              roleData={viewRole}
              onClose={() => setViewRole(null)}
              onEdit={(r) => {
                setViewRole(null);
                setEditRole(r);
              }}
            />
          </div>
        </>
      )}

      {editRole && (
        <>
          <div className="drawer-overlay" onClick={() => setEditRole(null)} />
          <div className="drawer-panel">
            <EditRole
              roleData={editRole}
              onClose={() => setEditRole(null)}
              onSuccess={() => {
                setEditRole(null);
                load();
              }}
            />
          </div>
        </>
      )}
    </div>
  );
}
